import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader } from './../../components/card/card.jsx';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import authService from '../../services/authService.js';
import { RefreshCw, Filter } from 'lucide-react';

const LoginAttempts = () => {
  const [attempts, setAttempts] = useState([]);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [onlyFailed, setOnlyFailed] = useState(false);

  const filteredAttempts = useMemo(() => {
    let list = attempts;
    if (onlyFailed) list = list.filter(a => !a.success);
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(a =>
      (a.username || '').toLowerCase().includes(q) ||
      (a.ip_address || '').toLowerCase().includes(q) ||
      (a.user_agent || '').toLowerCase().includes(q) ||
      (a.failure_reason || '').toLowerCase().includes(q)
    );
  }, [attempts, search, onlyFailed]);

  const fetchAttempts = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await authService.getLoginAttempts({ limit: limit + 1, offset });
      const arr = data?.attempts || [];
      setHasMore(arr.length > limit);
      setAttempts(arr.slice(0, limit));
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to fetch login attempts');
      setAttempts([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttempts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, offset]);

  const onRefresh = () => {
    setOffset(0);
    fetchAttempts();
  };

  const formatDateTime = (ts) => (ts ? new Date(ts).toLocaleString() : '');

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h1 mb-0">Login Attempts</h1>
        <div className="d-flex gap-2">
          <Button variant="outline" onClick={onRefresh} disabled={loading}>
            <RefreshCw className="me-2" /> Refresh
          </Button>
        </div>
      </div>

      <Card className="mb-3">
        <CardHeader className="d-flex align-items-center gap-3">
          <strong><Filter className="me-2" /> Filters</strong>
          <div className="ms-auto d-flex gap-2 align-items-center flex-wrap">
            <input
              type="text"
              className="form-control"
              style={{ minWidth: 240 }}
              placeholder="Search (username, IP, agent, reason)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="form-check">
              <input className="form-check-input" type="checkbox" id="onlyFailed" checked={onlyFailed} onChange={(e) => setOnlyFailed(e.target.checked)} />
              <label className="form-check-label" htmlFor="onlyFailed">Only failed</label>
            </div>
            <select
              className="form-select"
              value={limit}
              onChange={(e) => { setOffset(0); setLimit(parseInt(e.target.value, 10)); }}
            >
              {[25,50,100].map(n => <option key={n} value={n}>{n}/page</option>)}
            </select>
          </div>
        </CardHeader>
        <CardBody>
          {error && (
            <div className="alert alert-danger" role="alert">{error}</div>
          )}
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead>
                <tr>
                  <th style={{whiteSpace:'nowrap'}}>Time</th>
                  <th>Username</th>
                  <th>IP</th>
                  <th>User Agent</th>
                  <th>Success</th>
                  <th>Failure Reason</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center py-4">Loading...</td></tr>
                ) : filteredAttempts.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-4">No attempts found</td></tr>
                ) : (
                  filteredAttempts.map(attempt => (
                    <tr key={attempt.id}>
                      <td style={{whiteSpace:'nowrap'}}>{formatDateTime(attempt.timestamp)}</td>
                      <td>{attempt.username}</td>
                      <td>{attempt.ip_address || '-'}</td>
                      <td>
                        <span title={attempt.user_agent || ''} className="text-truncate d-inline-block" style={{maxWidth: 360}}>
                          {attempt.user_agent || '-'}
                        </span>
                      </td>
                      <td>
                        <Badge variant={attempt.success ? 'default' : 'destructive'}>
                          {attempt.success ? 'Yes' : 'No'}
                        </Badge>
                      </td>
                      <td>{attempt.failure_reason || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted small">Showing {filteredAttempts.length} item(s)</div>
            <div className="btn-group">
              <Button variant="outline" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - limit))}>Prev</Button>
              <Button variant="outline" disabled={!hasMore || loading} onClick={() => setOffset(offset + limit)}>Next</Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default LoginAttempts;